import React from "react";
import { IMG_CDN_URL } from "../constants";
import { Restaurant } from "../types";

interface RestaurantCardProps {
    restaurant: Restaurant;
}

const RestaurantCard: React.FC<RestaurantCardProps> = ({ restaurant }) => {
    const {
        cloudinaryImageId,
        name,
        cuisines,
        areaName,
        locality,
        avgRating,
        avgRatingString,
        totalRatingsString,
        sla,
        costForTwo,
        isOpen,
        aggregatedDiscountInfoV3,
        veg,
        badges,
        availability
    } = restaurant;

    const getRatingColor = (rating: number): string => {
        if (rating >= 4) return "bg-green-600"; // Cleaner green
        if (rating >= 3) return "bg-orange-500";
        return "bg-red-500";
    };

    // Handle cuisines as array or string and sort for better display
    const cuisineText = Array.isArray(cuisines) ? cuisines.slice(0, 3).join(", ") + (cuisines.length > 3 ? "..." : "") : cuisines;

    // Get discount text
    const discountText = aggregatedDiscountInfoV3?.header
        ? `${aggregatedDiscountInfoV3.header}${aggregatedDiscountInfoV3.subHeader ? ' ' + aggregatedDiscountInfoV3.subHeader : ''}`
        : null;

    return (
        <article className="rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer group relative bg-white dark:bg-gray-800/80 dark:backdrop-blur-sm shadow-sm hover:shadow-xl hover:-translate-y-1 border border-gray-100 dark:border-white/5 h-full flex flex-col">
            {/* Closed Overlay */}
            {isOpen === false && (
                <div className="absolute inset-0 bg-gray-900/60 z-20 flex items-center justify-center backdrop-blur-[1px]">
                    <div className="bg-white/95 dark:bg-black/90 px-4 py-2 rounded-lg shadow-xl text-center">
                        <span className="text-gray-800 dark:text-gray-200 font-bold text-sm block">Opens at</span>
                        <span className="text-orange-500 font-bold">{availability?.nextCloseTime ? new Date(availability.nextCloseTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "Soon"}</span>
                    </div>
                </div>
            )}

            {/* Image Container */}
            <div className="relative aspect-[4/3] overflow-hidden">
                <img
                    src={IMG_CDN_URL + cloudinaryImageId}
                    alt={name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-in-out"
                    loading="lazy"
                />

                {/* Gradient for text readability */}
                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 to-transparent pointer-events-none"></div>



                {/* Discount Badge (Bottom Left) */}
                {discountText && (
                    <div className="absolute bottom-3 left-3 right-3 z-10">
                        <div className="font-extrabold text-xl text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] leading-none tracking-tight">
                            {aggregatedDiscountInfoV3?.header}
                            <span className="text-base font-bold ml-1 opacity-95 block sm:inline sm:mt-0 mt-0.5">
                                {aggregatedDiscountInfoV3?.subHeader}
                            </span>
                        </div>
                    </div>
                )}

                {/* Rating Badge (Bottom Right used to be top right, moved to cleanup) */}
                {/* Keeping standard rating position but refined */}
            </div>

            {/* Content */}
            <div className="p-4 flex-1 flex flex-col">
                <div className="flex justify-between items-start mb-1 gap-2">
                    <h3 className="font-bold text-lg text-gray-800 dark:text-gray-100 line-clamp-1 flex-1 group-hover:text-orange-500 transition-colors">
                        {name}
                    </h3>

                    {/* Rating Pill */}
                    {avgRating && (
                        <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md ${getRatingColor(avgRating)} text-white shadow-sm flex-shrink-0`}>
                            <span className="text-xs font-bold leading-none">{avgRatingString || avgRating}</span>
                            <i className="fa-solid fa-star text-[8px] leading-none"></i>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2 mb-2.5">
                    {/* Veg/Non-Veg Indicator */}
                    {veg ? (
                        <div className="w-4 h-4 border border-green-600 p-0.5 rounded-sm flex items-center justify-center flex-shrink-0" title="Vegetarian">
                            <div className="w-full h-full bg-green-600 rounded-full"></div>
                        </div>
                    ) : (
                        // Optional: Add Non-Veg indicator if you want, usually Swiggy just shows Veg
                        <div className="w-4 h-4 border border-red-600 p-0.5 rounded-sm flex items-center justify-center flex-shrink-0" title="Non-Vegetarian">
                            <i className="fa-solid fa-play text-[8px] text-red-600 rotate-[30deg] ml-[1px]"></i>
                        </div>
                    )}
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-medium line-clamp-1 flex-1">
                        {cuisineText}
                    </p>
                </div>

                {/* Location & Time */}
                <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mb-3.5">
                    <div className="flex items-center gap-1 min-w-0">
                        <i className="fa-solid fa-location-dot text-gray-400"></i>
                        <span className="truncate">{areaName || locality}</span>
                    </div>
                    <div className="w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-600 flex-shrink-0"></div>
                    <span className="font-semibold text-gray-700 dark:text-gray-300 flex-shrink-0">
                        {sla?.slaString || `${sla?.deliveryTime} mins`}
                    </span>
                </div>

                {/* Divider */}
                <div className="w-full h-[1px] bg-gray-100 dark:bg-white/5 mb-3"></div>

                {/* Bottom Stats */}
                <div className="mt-auto flex items-center justify-between text-xs font-medium text-gray-500 dark:text-gray-400">
                    <span>{costForTwo ?? "₹200 for two"}</span>
                    {sla?.lastMileTravelString && (
                        <span className="flex items-center gap-1 bg-gray-50 dark:bg-white/5 px-2 py-1 rounded-full text-gray-600 dark:text-gray-300">
                            <i className="fa-solid fa-person-biking text-orange-500"></i>
                            {sla.lastMileTravelString}
                        </span>
                    )}
                </div>
            </div>
        </article>
    );
};

export default RestaurantCard;

